import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from 'shared/lib/utils';
import type { DocumentKey } from 'features/onboarding/types/onboarding.types';

interface DocumentInstructionsProps {
  docKey: DocumentKey;
}

/**
 * Collapsible "how do I get this document?" panel shown next to a document
 * upload slot. Pulls a localized title + ordered list of steps from the
 * `documentInstructions.<docKey>` i18n namespace. Renders nothing if no
 * instructions are defined for the document.
 */
const DocumentInstructions: React.FC<DocumentInstructionsProps> = ({ docKey }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const steps = t(`documentInstructions.${docKey}.steps`, {
    returnObjects: true,
    defaultValue: [],
  }) as unknown;
  const stepList = Array.isArray(steps) ? (steps as string[]) : [];

  if (stepList.length === 0) return null;

  const title = t(`documentInstructions.${docKey}.title`, {
    defaultValue: t('documentInstructions.defaultTitle', {
      defaultValue: 'How do I get this document?',
    }),
  }) as string;

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2.5 px-3.5 py-3 text-start"
      >
        <span className="w-7 h-7 rounded-lg bg-white text-blue-600 flex items-center justify-center flex-shrink-0">
          <HelpCircle size={15} />
        </span>
        <span className="flex-1 text-sm font-medium text-blue-900">{title}</span>
        <ChevronDown
          size={18}
          className={cn(
            'text-blue-500 transition-transform flex-shrink-0',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <ol className="px-4 pb-4 ps-10 flex flex-col gap-2 list-decimal text-sm text-gray-700 leading-relaxed">
          {stepList.map((step, i) => (
            <li key={i} className="ps-1">
              {step}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default DocumentInstructions;
